import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Loader2, Check, Building2, Compass, Settings2, Rocket,
  Bike, Ship, Waves, Anchor, Mountain, Wind, Flag, Map,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ACTIVITIES = [
  { id: "quad_buggy", label: "Randonnées Quad & Buggy", icon: Bike },
  { id: "jetski_boat", label: "Jet-ski & Base Nautique", icon: Waves },
  { id: "boat", label: "Balades en Bateau & Yachting", icon: Ship },
  { id: "diving", label: "Plongée & Snorkeling", icon: Anchor },
  { id: "camel_horse", label: "Méchouis & Chameau / Cheval", icon: Mountain },
  { id: "paragliding", label: "Parapente & Activités Aériennes", icon: Wind },
  { id: "karting", label: "Karting & Moteurs", icon: Flag },
  { id: "tours", label: "Visites & Tours Guidés", icon: Map },
];
const CURRENCIES = ["MAD", "EUR", "AED", "USD", "GBP"];
const LANGS = [{ v: "fr", l: "Français" }, { v: "en", l: "English" }, { v: "ar", l: "العربية" }, { v: "es", l: "Español" }];
const TIMEZONES = ["Africa/Casablanca", "Europe/Paris", "Asia/Dubai", "Europe/London", "Europe/Madrid"];
const STEPS = [
  { n: 1, title: "Entreprise", icon: Building2 },
  { n: 2, title: "Activités", icon: Compass },
  { n: 3, title: "Configuration", icon: Settings2 },
  { n: 4, title: "Finalisation", icon: Rocket },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { reload, user } = useAuth();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    company_name: "", legal_name: "", country: "MA", city: "", phone: "",
    email: user?.email || "", activities: [],
    default_currency: "MAD", default_language: "fr",
    timezone: "Africa/Casablanca", default_deposit_pct: 30,
  });
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleAct = (id) =>
    setForm((f) => ({
      ...f,
      activities: f.activities.includes(id)
        ? f.activities.filter((a) => a !== id)
        : [...f.activities, id],
    }));

  const canNext =
    (step === 1 && form.company_name && form.city) ||
    (step === 2 && form.activities.length > 0) ||
    step === 3 || step === 4;

  const finish = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post("/api/onboarding", form);
      await reload();
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  const input =
    "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30";

  return (
    <div className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto max-w-2xl">
        {/* Stepper */}
        <div className="mb-10 flex items-center justify-between">
          {STEPS.map((s, i) => {
            const active = s.n === step;
            const done = s.n < step;
            const Icon = s.icon;
            return (
              <React.Fragment key={s.n}>
                <div className="flex flex-col items-center gap-2" data-testid={`onboarding-step-${s.n}`}>
                  <div className={`flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors ${
                    done ? "border-primary bg-primary text-white"
                    : active ? "border-primary bg-emerald-50 text-primary"
                    : "border-slate-200 bg-white text-slate-400"}`}>
                    {done ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                  </div>
                  <span className={`text-xs font-medium ${active || done ? "text-slate-900" : "text-slate-400"}`}>{s.title}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`mx-2 h-0.5 flex-1 rounded ${s.n < step ? "bg-primary" : "bg-slate-200"}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8 animate-fade-up">
          {error && <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700">{error}</div>}

          {step === 1 && (
            <div className="space-y-4" data-testid="onboarding-company">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Informations de l'entreprise</h2>
                <p className="mt-1 text-sm text-slate-500">Présentez votre société.</p>
              </div>
              <Field label="Nom commercial *"><input className={input} data-testid="onboarding-company-name" value={form.company_name} onChange={(e) => upd("company_name", e.target.value)} placeholder="Atlas Adventure" /></Field>
              <Field label="Raison sociale"><input className={input} value={form.legal_name} onChange={(e) => upd("legal_name", e.target.value)} placeholder="Atlas Adventure SARL" /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Pays">
                  <SelectField value={form.country} onChange={(v) => upd("country", v)} testid="onboarding-country"
                    options={["MA", "FR", "AE", "GB", "ES", "US"].map((c) => ({ v: c, l: c }))} />
                </Field>
                <Field label="Ville *"><input className={input} data-testid="onboarding-city" value={form.city} onChange={(e) => upd("city", e.target.value)} placeholder="Agadir" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Téléphone"><input className={input} value={form.phone} onChange={(e) => upd("phone", e.target.value)} placeholder="+212 ..." /></Field>
                <Field label="E-mail"><input className={input} value={form.email} onChange={(e) => upd("email", e.target.value)} /></Field>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="onboarding-activities">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Activités proposées</h2>
                <p className="mt-1 text-sm text-slate-500">Sélectionnez toutes vos activités.</p>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {ACTIVITIES.map((a) => {
                  const on = form.activities.includes(a.id);
                  const Icon = a.icon;
                  return (
                    <button key={a.id} type="button" onClick={() => toggleAct(a.id)} data-testid={`onboarding-activity-${a.id}`}
                      className={`flex items-center gap-3 rounded-xl border p-3.5 text-left transition-colors ${on ? "border-primary bg-emerald-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${on ? "bg-primary text-white" : "bg-slate-100 text-slate-500"}`}>
                        <Icon className="h-4.5 w-4.5" />
                      </div>
                      <span className={`text-sm font-medium ${on ? "text-slate-900" : "text-slate-600"}`}>{a.label}</span>
                      {on && <Check className="ml-auto h-4 w-4 text-primary" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="onboarding-settings">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Configuration opérationnelle</h2>
                <p className="mt-1 text-sm text-slate-500">Devise, langue et fuseau horaire.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Devise principale">
                  <SelectField value={form.default_currency} onChange={(v) => upd("default_currency", v)} testid="onboarding-currency"
                    options={CURRENCIES.map((c) => ({ v: c, l: c }))} />
                </Field>
                <Field label="Langue de l'interface">
                  <SelectField value={form.default_language} onChange={(v) => upd("default_language", v)} testid="onboarding-language"
                    options={LANGS.map((l) => ({ v: l.v, l: l.l }))} />
                </Field>
              </div>
              <Field label="Fuseau horaire">
                <SelectField value={form.timezone} onChange={(v) => upd("timezone", v)} testid="onboarding-timezone"
                  options={TIMEZONES.map((t) => ({ v: t, l: t }))} />
              </Field>
              <Field label={`Acompte par défaut : ${form.default_deposit_pct}%`}>
                <input type="range" min="0" max="100" step="5" value={form.default_deposit_pct} onChange={(e) => upd("default_deposit_pct", Number(e.target.value))} className="w-full accent-emerald-600" />
              </Field>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5 text-center" data-testid="onboarding-finish">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-primary">
                <Rocket className="h-7 w-7" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900">Tout est prêt !</h2>
                <p className="mt-1 text-sm text-slate-500">Vérifiez le récapitulatif et lancez votre espace.</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-left text-sm">
                <Row k="Entreprise" v={form.company_name || "—"} />
                <Row k="Ville" v={`${form.city}, ${form.country}`} />
                <Row k="Activités" v={form.activities.length ? `${form.activities.length} sélectionnée(s)` : "—"} />
                <Row k="Devise" v={form.default_currency} />
                <Row k="Langue" v={form.default_language.toUpperCase()} />
                <Row k="Fuseau" v={form.timezone} last />
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between">
            <button type="button" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1}
              data-testid="onboarding-prev-button"
              className="rounded-lg px-4 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 disabled:invisible">
              Précédent
            </button>
            {step < 4 ? (
              <button type="button" onClick={() => canNext && setStep((s) => s + 1)} disabled={!canNext}
                data-testid="onboarding-next-button"
                className="rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:opacity-50">
                Continuer
              </button>
            ) : (
              <button type="button" onClick={finish} disabled={busy}
                data-testid="onboarding-finish-button"
                className="flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:opacity-60">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                Créer mon organisation
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>
    {children}
  </div>
);
const SelectField = ({ value, onChange, options, testid, placeholder }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="w-full rounded-lg border-slate-300 py-2.5 text-sm">
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      {options.map((o) => (
        <SelectItem key={o.v} value={o.v} data-testid={`${testid}-${o.v}`}>{o.l}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);
const Row = ({ k, v, last }) => (
  <div className={`flex justify-between py-2 ${last ? "" : "border-b border-slate-200"}`}>
    <span className="text-slate-500">{k}</span>
    <span className="font-medium text-slate-900">{v}</span>
  </div>
);
